// AlertasPage.js — /alertas: historial completo de alertas del alumno en este proyecto.
// Se lee como un registro: una línea de tiempo por día (encabezado fijo al desplazarse),
// cada alerta con su hora, un punto del color de su tipo y sus acciones. A la izquierda,
// los filtros (búsqueda, sin leer, tipo y periodo); en celular pasan arriba.
// «Ver más» trae de 20 en 20; se puede marcar como leída, eliminar una o varias.
// Lo que se limpia en el panel de la campana sigue aquí; lo que se elimina aquí ya no
// aparece en ningún lado. En proyectos con portada estilo streaming (Learning) va sobre
// el banner de portadas de los cursos, con su diseño oscuro.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { useProject } from '../contexts/ProjectContext';
import { alertasService } from '../services/api';
import { TIPOS, tipoDe, diaDe, horaDe, esExterno, IcoCampana, IcoPin } from '../components/alertas/alertasComun';
import MuroCursos from '../components/landing/sections/MuroCursos';

const aFecha = (f) => new Date(String(f).replace(' ', 'T') + 'Z');
const fechaLarga = (f) => {
  const t = aFecha(f).toLocaleDateString('es-CO', { weekday: 'long', day: 'numeric', month: 'long', timeZone: 'America/Bogota' });
  return t.charAt(0).toUpperCase() + t.slice(1);
};
const fechaCorta = (f) => aFecha(f).toLocaleDateString('es-CO', { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'America/Bogota' });

const PERIODOS = [{ v: 0, t: 'Todo' }, { v: 7, t: 'Últimos 7 días' }, { v: 30, t: 'Últimos 30 días' }];

const Raiz = styled.div`
  --fondo: ${p => p.$t.fondo}; --texto: ${p => p.$t.texto}; --suave: ${p => p.$t.suave}; --linea: ${p => p.$t.linea};
  --tarjeta: ${p => p.$t.tarjeta}; --acento: ${p => p.$acento}; --cab: ${p => (p.$oscuro ? '78px' : '72px')};
  background: var(--fondo); color: var(--texto); min-height: 100vh;
  ${p => p.$fuente && `font-family: ${p.$fuente};`}
`;

// ---------- Cabecera ----------
const Cabecera = styled.header`
  position: relative; overflow: hidden; isolation: isolate;
  min-height: ${p => (p.$banner ? 'clamp(340px, 46vh, 460px)' : 'auto')}; display: flex; flex-direction: column; justify-content: flex-end;
  padding: clamp(7.5rem, 11vw, 8.5rem) 4vw clamp(1.8rem, 3.5vw, 2.6rem);
  border-bottom: 1px solid var(--linea);
  .eyebrow { display: inline-flex; align-items: center; gap: .6rem; font-size: .95rem; font-weight: 600; color: var(--suave); }
  .eyebrow::before { content: ''; width: 28px; height: 2px; background: var(--acento); }
  h1 { margin: .8rem 0 0; font-size: ${p => (p.$banner ? 'clamp(2.8rem, 7vw, 5.4rem)' : 'clamp(2rem, 5vw, 3rem)')}; font-weight: 800;
    ${p => p.$banner && 'font-stretch: 76%;'} line-height: .95; letter-spacing: -.02em; color: var(--texto); }
  dl { display: flex; flex-wrap: wrap; gap: .6rem; margin: 1.4rem 0 0; }
  dl div { display: flex; align-items: baseline; gap: .45rem; padding: .5rem 1rem; border-radius: 999px;
    background: ${p => (p.$banner ? 'rgba(0,0,0,.42)' : 'var(--tarjeta)')}; box-shadow: inset 0 0 0 1px var(--linea);
    ${p => p.$banner && 'backdrop-filter: blur(8px); -webkit-backdrop-filter: blur(8px);'} }
  dt { order: 2; font-size: .88rem; color: var(--suave); }
  dd { margin: 0; font-size: 1.15rem; font-weight: 800; color: var(--texto); font-variant-numeric: tabular-nums; }
  dd.acento { color: var(--acento); }
`;

// ---------- Cuerpo: filtros + registro ----------
const Cuerpo = styled.div`
  display: grid; grid-template-columns: 250px minmax(0, 1fr); gap: clamp(1.5rem, 4vw, 3.5rem);
  padding: clamp(1.6rem, 3.5vw, 2.6rem) 4vw clamp(3rem, 6vw, 5rem); max-width: 1240px;
  > * { min-width: 0; }
  @media (max-width: 900px) { grid-template-columns: 1fr; gap: 1.2rem; }
`;
const Filtros = styled.aside`
  position: sticky; top: calc(var(--cab) + 1rem); align-self: start; display: flex; flex-direction: column; gap: 1.4rem;
  input[type='search'] { width: 100%; box-sizing: border-box; padding: .65rem .85rem; border-radius: 8px; border: 1px solid var(--linea);
    background: var(--tarjeta); color: var(--texto); font: inherit; font-size: .92rem; }
  input[type='search']::placeholder { color: var(--suave); }
  input[type='search']:focus { outline: 2px solid var(--acento); outline-offset: 0; border-color: transparent; }
  h2 { margin: 0 0 .45rem; font-size: .82rem; font-weight: 700; color: var(--suave); }
  .opciones { display: flex; flex-direction: column; gap: 2px; }
  .opciones button { display: flex; align-items: center; gap: .6rem; width: 100%; padding: .5rem .6rem; border: 0; border-radius: 6px;
    background: transparent; color: var(--texto); font: inherit; font-size: .92rem; text-align: left; cursor: pointer; }
  .opciones button:hover { background: var(--tarjeta); }
  .opciones button[aria-pressed='true'] { background: var(--tarjeta); box-shadow: inset 2px 0 0 var(--acento); font-weight: 700; }
  .opciones button:focus-visible { outline: 2px solid var(--acento); outline-offset: 1px; }
  .opciones i { flex: none; width: 9px; height: 9px; border-radius: 50%; }
  .opciones small { margin-left: auto; color: var(--suave); font-size: .82rem; font-variant-numeric: tabular-nums; }
  .accion { align-self: flex-start; padding: .5rem .8rem; border-radius: 8px; border: 1px solid var(--linea); background: transparent;
    color: var(--texto); font: inherit; font-size: .86rem; font-weight: 650; cursor: pointer; }
  .accion:hover:not(:disabled) { border-color: var(--acento); }
  .accion:disabled { opacity: .45; cursor: default; }
  @media (max-width: 900px) {
    position: static; gap: .7rem;
    section { min-width: 0; }
    h2 { display: none; }
    .opciones { flex-direction: row; gap: .4rem; overflow-x: auto; scrollbar-width: none; padding-bottom: 2px; }
    .opciones::-webkit-scrollbar { display: none; }
    .opciones button { width: auto; flex: none; padding: .4rem .75rem; border-radius: 999px; border: 1px solid var(--linea); font-size: .85rem; }
    .opciones button[aria-pressed='true'] { background: var(--texto); color: var(--fondo); box-shadow: none; border-color: var(--texto); }
    .opciones button[aria-pressed='true'] small { color: inherit; opacity: .7; }
    .opciones small { margin-left: .2rem; }
  }
`;

// Un día del registro: encabezado fijo y la línea de tiempo.
const Dia = styled.section`
  & + & { margin-top: 1.6rem; }
  > header { position: sticky; top: var(--cab); z-index: 2; display: flex; align-items: baseline; gap: .7rem; padding: .7rem 0 .6rem;
    background: var(--fondo); border-bottom: 1px solid var(--linea); }
  > header h2 { margin: 0; font-size: 1.25rem; font-weight: 800; ${p => p.$oscuro && 'font-stretch: 85%;'} color: var(--texto); }
  > header span { font-size: .88rem; color: var(--suave); }
  > header small { margin-left: auto; font-size: .82rem; color: var(--suave); font-variant-numeric: tabular-nums; }
  ol { list-style: none; margin: 0; padding: 0; }
`;
const Registro = styled.li`
  position: relative; display: grid; grid-template-columns: 18px 74px 30px minmax(0, 1fr) auto; gap: 0 .8rem; align-items: start;
  padding: .95rem .4rem .95rem .2rem; border-radius: 8px;
  &:hover { background: var(--tarjeta); }
  /* La línea vertical que une los puntos del día. */
  .linea { position: relative; align-self: stretch; display: flex; justify-content: center; }
  .linea::before { content: ''; position: absolute; top: -.95rem; bottom: -.95rem; width: 2px; background: var(--linea); }
  &:first-child .linea::before { top: 14px; }
  &:last-child .linea::before { bottom: calc(100% - 14px); }
  .punto { position: relative; z-index: 1; width: 28px; height: 28px; border-radius: 50%; display: grid; place-items: center;
    color: ${p => (p.$nueva ? 'var(--fondo)' : p.$c)}; background: ${p => (p.$nueva ? p.$c : 'var(--fondo)')}; box-shadow: 0 0 0 2px ${p => p.$c}; }
  .punto svg { width: 14px; height: 14px; stroke-width: 2.4; }
  input[type='checkbox'] { margin: 6px 0 0; width: 16px; height: 16px; accent-color: var(--acento); opacity: ${p => (p.$sel ? 1 : 0)}; transition: opacity .15s; }
  &:hover input[type='checkbox'], input[type='checkbox']:focus-visible, input[type='checkbox']:checked { opacity: 1; }
  @media (hover: none) { input[type='checkbox'] { opacity: 1; } }
  .hora { padding-top: 5px; font-size: .86rem; color: var(--suave); font-variant-numeric: tabular-nums; white-space: nowrap; }
  .tit { margin: 0; padding-top: 3px; font-size: .98rem; line-height: 1.4; font-weight: ${p => (p.$nueva ? 750 : 550)}; color: var(--texto); }
  .tit b { display: inline-block; margin-left: .5rem; padding: .05rem .45rem; border-radius: 4px; vertical-align: 2px;
    background: var(--acento); color: #fff; font-size: .68rem; font-weight: 800; }
  .msg { margin: .2rem 0 0; max-width: 68ch; font-size: .9rem; line-height: 1.5; color: var(--suave); }
  .meta { display: flex; flex-wrap: wrap; align-items: center; gap: .3rem .9rem; margin-top: .45rem; font-size: .82rem; color: var(--suave); }
  .meta .tipo { color: ${p => p.$c}; font-weight: 650; }
  .meta button { border: 0; background: none; padding: 0; font: inherit; font-weight: 700; color: var(--texto); cursor: pointer; text-decoration: underline;
    text-decoration-color: var(--acento); text-underline-offset: 3px; text-decoration-thickness: 2px; }
  .acciones { display: flex; gap: .2rem; opacity: 0; transition: opacity .15s; }
  &:hover .acciones, .acciones:focus-within { opacity: 1; }
  @media (hover: none) { .acciones { opacity: 1; } }
  .acciones button { border: 0; background: none; color: var(--suave); height: 30px; padding: 0 .5rem; border-radius: 6px; font: inherit; font-size: .8rem; font-weight: 650; cursor: pointer; }
  .acciones button:hover { background: rgba(127,127,127,.15); color: var(--texto); }
  .acciones .x:hover { color: #f87171; }
  button:focus-visible { outline: 2px solid var(--acento); outline-offset: 1px; }
  .meta .h { display: none; }
  @media (max-width: 600px) {
    grid-template-columns: 28px minmax(0, 1fr); gap: 0 .7rem; padding-left: 0;
    input[type='checkbox'] { display: none; }
    .hora { display: none; }
    .meta .h { display: inline; }
    .acciones { grid-column: 2; opacity: 1; margin: .35rem 0 0 -.5rem; }
  }
`;
// Fijadas por el proyecto: siempre arriba, aparte del registro, sin eliminar.
const Fijadas = styled.section`
  margin-bottom: 1.8rem;
  > h2 { display: flex; align-items: center; gap: .45rem; margin: 0 0 .6rem; font-size: .95rem; font-weight: 700; color: var(--texto); }
  > h2 svg { color: var(--acento); }
  ul { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: .6rem; }
`;
const Fija = styled.li`
  display: grid; grid-template-columns: 36px minmax(0, 1fr) auto; gap: .9rem; align-items: start; padding: 1rem 1.1rem; border-radius: 10px;
  background: color-mix(in srgb, var(--acento) 8%, var(--tarjeta)); box-shadow: inset 3px 0 0 var(--acento), inset 0 0 0 1px var(--linea);
  .ico { width: 36px; height: 36px; border-radius: 10px; display: grid; place-items: center; color: ${p => p.$c}; background: color-mix(in srgb, ${p => p.$c} 16%, transparent); }
  .tit { margin: 0; padding-top: 2px; font-size: 1.02rem; font-weight: 700; line-height: 1.4; color: var(--texto); }
  .msg { margin: .25rem 0 0; max-width: 70ch; font-size: .92rem; line-height: 1.5; color: var(--suave); }
  .meta { display: flex; flex-wrap: wrap; gap: .3rem .9rem; margin-top: .5rem; font-size: .82rem; color: var(--suave); }
  .meta button { border: 0; background: none; padding: 0; font: inherit; font-weight: 700; color: var(--texto); cursor: pointer; text-decoration: underline;
    text-decoration-color: var(--acento); text-underline-offset: 3px; text-decoration-thickness: 2px; }
  .leer { border: 0; background: none; color: var(--suave); height: 30px; padding: 0 .5rem; border-radius: 6px; font: inherit; font-size: .8rem; font-weight: 650; cursor: pointer; }
  .leer:hover { background: rgba(127,127,127,.15); color: var(--texto); }
  button:focus-visible { outline: 2px solid var(--acento); outline-offset: 1px; }
  @media (max-width: 600px) { grid-template-columns: 32px minmax(0, 1fr); .leer { grid-column: 2; justify-self: start; margin-left: -.5rem; } }
`;
const Vacio = styled.div`
  padding: 3.5rem 1rem; text-align: center; color: var(--suave); border: 1px dashed var(--linea); border-radius: 10px;
  svg { width: 40px; height: 40px; opacity: .4; margin-bottom: .6rem; }
  b { display: block; margin-bottom: .3rem; color: var(--texto); font-size: 1.05rem; }
  a { color: var(--texto); font-weight: 700; text-decoration-color: var(--acento); }
`;
const Pie = styled.div`
  display: flex; align-items: center; justify-content: space-between; gap: 1rem; flex-wrap: wrap; margin-top: 1.4rem; padding-top: 1rem;
  border-top: 1px solid var(--linea); font-size: .88rem; color: var(--suave);
  button { padding: .55rem 1.1rem; border-radius: 8px; border: 1px solid var(--linea); background: transparent; color: var(--texto);
    font: inherit; font-weight: 700; cursor: pointer; }
  button:hover:not(:disabled) { border-color: var(--acento); }
  button:disabled { opacity: .5; }
`;
const BarraSel = styled.div`
  position: sticky; bottom: 1rem; z-index: 3; margin-top: 1rem; display: flex; align-items: center; justify-content: space-between; gap: 1rem;
  padding: .7rem .9rem; border-radius: 10px; background: var(--texto); color: var(--fondo); font-size: .9rem; font-weight: 650;
  box-shadow: 0 12px 30px -10px rgba(0,0,0,.5);
  span { display: flex; gap: .5rem; }
  button { border: 0; border-radius: 6px; padding: .45rem .8rem; font: inherit; font-weight: 700; cursor: pointer; background: transparent; color: inherit; }
  button.leer { background: rgba(127,127,127,.25); }
  button.borrar { background: #dc2626; color: #fff; }
`;
// Confirmación de borrado en la misma página (sin ventana del navegador).
const Confirmar = styled.div`
  position: sticky; bottom: 1rem; z-index: 3; margin-top: 1rem; display: flex; align-items: center; justify-content: space-between; gap: 1rem; flex-wrap: wrap;
  padding: .8rem 1rem; border-radius: 10px; background: #2a1414; color: #fde2e2; box-shadow: inset 0 0 0 1px rgba(248,113,113,.35), 0 12px 30px -10px rgba(0,0,0,.5);
  font-size: .9rem;
  span { display: flex; gap: .5rem; }
  button { border: 0; border-radius: 6px; padding: .45rem .8rem; font: inherit; font-weight: 700; cursor: pointer; background: transparent; color: inherit; }
  button.si { background: #dc2626; color: #fff; }
`;

const AlertasPage = () => {
  const { proyecto } = useProject();
  const navigate = useNavigate();
  const pid = proyecto?.id;
  const cat = (proyecto?.config_json?.landing_secciones || []).find(s => s.tipo === 'catalogo_streaming' && s.visible !== false)?.config;
  const oscuro = !!cat;
  const acento = cat?.color_acento || proyecto?.color_primario || '#0f355b';
  const tema = oscuro
    ? { fondo: cat.color_fondo || '#141414', texto: '#eceff2', suave: '#9aa3ad', linea: 'rgba(255,255,255,.1)', tarjeta: 'rgba(255,255,255,.045)' }
    : { fondo: '#f6f8fb', texto: '#16202b', suave: '#5f6b78', linea: '#e3e8ef', tarjeta: '#fff' };

  const [tipo, setTipo] = useState('');
  const [estado, setEstado] = useState('');
  const [dias, setDias] = useState(0);
  const [q, setQ] = useState('');
  const [busqueda, setBusqueda] = useState('');
  const [datos, setDatos] = useState(null);
  const [alertas, setAlertas] = useState([]);
  const [pagina, setPagina] = useState(1);
  const [cargando, setCargando] = useState(false);
  const [sel, setSel] = useState(() => new Set());
  const [porBorrar, setPorBorrar] = useState(null);

  useEffect(() => { const t = setTimeout(() => setBusqueda(q.trim()), 300); return () => clearTimeout(t); }, [q]);

  const cargar = useCallback((pag) => {
    if (!pid) return;
    setCargando(true);
    alertasService.historial(pid, { tipo: tipo || undefined, q: busqueda || undefined, estado: estado || undefined, dias: dias || undefined, pagina: pag })
      .then(r => { setDatos(r.data); setAlertas(a => (pag === 1 ? r.data.alertas : [...a, ...r.data.alertas])); setPagina(pag); })
      .catch(() => setDatos(d => d || { error: true }))
      .finally(() => setCargando(false));
  }, [pid, tipo, busqueda, estado, dias]);
  useEffect(() => { setSel(new Set()); setPorBorrar(null); cargar(1); }, [cargar]);

  const grupos = useMemo(() => {
    const g = [];
    alertas.forEach(a => { const d = diaDe(a.fecha); if (!g.length || g[g.length - 1].dia !== d) g.push({ dia: d, fecha: a.fecha, items: [] }); g[g.length - 1].items.push(a); });
    return g;
  }, [alertas]);

  const leer = (ids) => alertasService.leer(pid, ids).then(r => {
    setAlertas(a => a.map(x => (!ids || ids.includes(x.id) ? { ...x, leida: true } : x)));
    setDatos(d => ({ ...d, sin_leer: r.data?.sin_leer ?? 0, fijadas: (d?.fijadas || []).map(x => (ids && ids.includes(x.id) ? { ...x, leida: true } : x)) }));
    setSel(new Set());
  });
  const borrar = (ids) => alertasService.borrar(pid, ids).then(() => {
    setAlertas(a => a.filter(x => !ids.includes(x.id)));
    setDatos(d => ({ ...d, total: Math.max(0, (d?.total || 0) - ids.length) }));
    setSel(new Set()); setPorBorrar(null);
  });
  const alternar = (id) => setSel(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const ir = (a) => { alertasService.clic(pid, a.id).catch(() => {}); if (esExterno(a.enlace)) window.open(a.enlace, '_blank', 'noopener'); else navigate(a.enlace.replace(window.location.origin, '')); };

  const porTipo = datos?.por_tipo || {};
  const totalTodos = Object.values(porTipo).reduce((s, n) => s + n, 0);
  const tiposVisibles = Object.keys(TIPOS).filter(k => porTipo[k]);
  const filtrando = !!(tipo || busqueda || estado || dias);
  const fuente = oscuro ? "'Bricolage Grotesque', 'Inter', system-ui, sans-serif" : null;
  // Sistema de alertas apagado en el proyecto: la página no existe para el alumno.
  if (proyecto && proyecto.config_json?.alertas?.activo !== true) return <Navigate to="/contenidos" replace />;

  return (
    <Raiz $t={tema} $acento={acento} $fuente={fuente} $oscuro={oscuro}>
      <Cabecera $banner={oscuro}>
        {oscuro && <MuroCursos proyecto={proyecto} fondo={tema.fondo} acento={acento} opacidad={0.55} />}
        <span className="eyebrow">Tu actividad</span>
        <h1>Historial de alertas</h1>
        {datos && !datos.error && (
          <dl>
            <div><dd>{totalTodos}</dd><dt>{totalTodos === 1 ? 'registro' : 'registros'}</dt></div>
            <div><dd className={datos.sin_leer ? 'acento' : ''}>{datos.sin_leer || 0}</dd><dt>sin leer</dt></div>
            {datos.desde && <div><dd style={{ fontSize: '.95rem' }}>{fechaCorta(datos.desde)}</dd><dt>primer registro</dt></div>}
          </dl>
        )}
      </Cabecera>

      <Cuerpo>
        <Filtros aria-label="Filtros del historial">
          <input type="search" value={q} onChange={e => setQ(e.target.value)} placeholder="Buscar en el historial" aria-label="Buscar en el historial" />
          <section>
            <h2>Estado</h2>
            <div className="opciones" role="group" aria-label="Estado">
              <button type="button" aria-pressed={!estado} onClick={() => setEstado('')}>Todas</button>
              <button type="button" aria-pressed={estado === 'sin_leer'} onClick={() => setEstado(estado === 'sin_leer' ? '' : 'sin_leer')}>
                Sin leer <small>{datos?.sin_leer || 0}</small>
              </button>
            </div>
          </section>
          {tiposVisibles.length > 1 && (
            <section>
              <h2>Tipo</h2>
              <div className="opciones" role="group" aria-label="Tipo">
                <button type="button" aria-pressed={!tipo} onClick={() => setTipo('')}>Todos los tipos <small>{totalTodos}</small></button>
                {tiposVisibles.map(k => (
                  <button key={k} type="button" aria-pressed={tipo === k} onClick={() => setTipo(tipo === k ? '' : k)}>
                    <i style={{ background: TIPOS[k].color }} />{TIPOS[k].nombre}<small>{porTipo[k]}</small>
                  </button>
                ))}
              </div>
            </section>
          )}
          <section>
            <h2>Periodo</h2>
            <div className="opciones" role="group" aria-label="Periodo">
              {PERIODOS.map(o => <button key={o.v} type="button" aria-pressed={dias === o.v} onClick={() => setDias(o.v)}>{o.t}</button>)}
            </div>
          </section>
          <button type="button" className="accion" onClick={() => leer(undefined)} disabled={!datos?.sin_leer}>Marcar todas como leídas</button>
        </Filtros>

        <div>
          {datos?.fijadas?.length > 0 && (
            <Fijadas aria-label="Alertas fijadas">
              <h2><IcoPin width="15" height="15" />Fijadas</h2>
              <ul>
                {datos.fijadas.map(a => {
                  const t = tipoDe(a.tipo);
                  return (
                    <Fija key={a.id} $c={t.color}>
                      <span className="ico" aria-hidden="true">{t.icono}</span>
                      <div>
                        <p className="tit">{a.titulo}</p>
                        {a.mensaje && <p className="msg">{a.mensaje}</p>}
                        <div className="meta">
                          <span>{diaDe(a.fecha)}, {horaDe(a.fecha)}</span>
                          {a.enlace && <button type="button" onClick={() => ir(a)}>{a.texto_enlace || 'Ver'}</button>}
                        </div>
                      </div>
                      {!a.leida && <button type="button" className="leer" onClick={() => leer([a.id])}>Marcar leída</button>}
                    </Fija>
                  );
                })}
              </ul>
            </Fijadas>
          )}
          {datos?.error ? <Vacio><b>No se pudo cargar el historial</b>Revisa tu conexión y vuelve a intentarlo.</Vacio>
            : !datos ? <Vacio>Cargando el historial…</Vacio>
            : !alertas.length ? (
              <Vacio><IcoCampana /><b>{filtrando ? 'Nada coincide con los filtros' : 'Tu historial está vacío'}</b>
                {filtrando ? 'Cambia el tipo, el periodo o las palabras de búsqueda.' : <>Aquí quedará registro de tus lecciones, evaluaciones y avisos. <Link to="/contenidos">Ir a mis cursos</Link></>}
              </Vacio>
            ) : grupos.map(g => (
              <Dia key={g.dia} $oscuro={oscuro}>
                <header>
                  <h2>{g.dia}</h2>
                  {(g.dia === 'Hoy' || g.dia === 'Ayer') && <span>{fechaLarga(g.fecha)}</span>}
                  <small>{g.items.length} {g.items.length === 1 ? 'registro' : 'registros'}</small>
                </header>
                <ol>
                  {g.items.map(a => {
                    const t = tipoDe(a.tipo);
                    return (
                      <Registro key={a.id} $c={t.color} $nueva={!a.leida} $sel={sel.size > 0}>
                        <input type="checkbox" checked={sel.has(a.id)} onChange={() => alternar(a.id)} aria-label={`Seleccionar «${a.titulo}»`} />
                        <time className="hora" dateTime={String(a.fecha).replace(' ', 'T') + 'Z'}>{horaDe(a.fecha)}</time>
                        <span className="linea" aria-hidden="true"><span className="punto">{t.icono}</span></span>
                        <div>
                          <p className="tit">{a.titulo}{!a.leida && <b>Nueva</b>}</p>
                          {a.mensaje && <p className="msg">{a.mensaje}</p>}
                          <div className="meta">
                            <span className="h">{horaDe(a.fecha)}</span>
                            <span className="tipo">{t.nombre}</span>
                            {a.enlace && <button type="button" onClick={() => ir(a)}>{a.texto_enlace || 'Ver'}</button>}
                          </div>
                        </div>
                        <div className="acciones">
                          {!a.leida && <button type="button" onClick={() => leer([a.id])}>Marcar leída</button>}
                          <button type="button" className="x" aria-label={`Eliminar «${a.titulo}»`} title="Eliminar del historial" onClick={() => setPorBorrar([a.id])}>Eliminar</button>
                        </div>
                      </Registro>
                    );
                  })}
                </ol>
              </Dia>
            ))}

          {datos && !datos.error && alertas.length > 0 && (
            <Pie>
              <span>Mostrando {alertas.length} de {datos.total}</span>
              {pagina < (datos.paginas || 1) && (
                <button type="button" onClick={() => cargar(pagina + 1)} disabled={cargando}>{cargando ? 'Cargando…' : 'Ver registros anteriores'}</button>
              )}
            </Pie>
          )}

          {porBorrar ? (
            <Confirmar role="alertdialog" aria-label="Confirmar eliminación">
              <div>{porBorrar.length === 1 ? '¿Eliminar este registro?' : `¿Eliminar ${porBorrar.length} registros?`} No se podrá recuperar.</div>
              <span>
                <button type="button" onClick={() => setPorBorrar(null)}>Cancelar</button>
                <button type="button" className="si" onClick={() => borrar(porBorrar)}>Eliminar</button>
              </span>
            </Confirmar>
          ) : sel.size > 0 && (
            <BarraSel role="region" aria-label="Registros seleccionados">
              <div>{sel.size} {sel.size === 1 ? 'seleccionado' : 'seleccionados'}</div>
              <span>
                <button type="button" onClick={() => setSel(new Set())}>Cancelar</button>
                <button type="button" className="leer" onClick={() => leer([...sel])}>Marcar leídas</button>
                <button type="button" className="borrar" onClick={() => setPorBorrar([...sel])}>Eliminar</button>
              </span>
            </BarraSel>
          )}
        </div>
      </Cuerpo>
    </Raiz>
  );
};

export default AlertasPage;
